/**
 * @fileoverview
 *  Pantalla que muestra el listado de pacientes de un veterinario.
 *  Soporta dos tipos: LemmonTag y Externo.
 */

import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getPacientesDeVet, actualizarPaciente, eliminarPaciente, getCitasDeVet } from "../../services/api";
import AddPatientModal from "./AddPatientModal";
import toast from "react-hot-toast";

import "bootstrap/dist/css/bootstrap.min.css";
import DynamicForm from "../../components/forms/DynamicForm";
import { RECORD_FIELDS } from "../../models/recordFields";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const LIMITE_EXTERNOS = 100;

// Brand colors
const COLORS = {
  primary: "#84cc16",
  primaryDark: "#65a30d",
  primaryLight: "#ecfccb",
  background: "#fafafa",
  cardBg: "#ffffff",
  textDark: "#1f2937",
  textMuted: "#6b7280",
  border: "#e5e7eb",
};

export default function VetPatientsScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const vetId = searchParams.get("vetId") || "";
  const token = searchParams.get("token") || "";
  const [letter, setLetter] = useState(searchParams.get("letra") || "");
  const navigate = useNavigate();

  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedPet, setSelectedPet] = useState(null);
  const [showFicha, setShowFicha] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingPet, setDeletingPet] = useState(null);
  const [showCitasModal, setShowCitasModal] = useState(false);
  const [citasPet, setCitasPet] = useState(null);

  const pacientesExternos = pacientes.filter((p) => p.tipo === "externo").length;
  const limiteAlcanzado = pacientesExternos >= LIMITE_EXTERNOS;

  useEffect(() => {
    const currentLetter = searchParams.get("letra") || "";
    setLetter(currentLetter);
  }, [searchParams]);

  useEffect(() => {
    loadPacientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vetId, letter]);

  async function loadPacientes() {
    if (!vetId) {
      setError("No se recibio vetId");
      setLoading(false);
      return;
    }
    setLoading(true);
    const resp = await getPacientesDeVet(vetId, letter);
    if (resp.error) {
      setError(resp.error);
    } else {
      setPacientes(resp.data || []);
    }
    setLoading(false);
  }

  function handleVolver() {
    navigate(`/vetPanel?token=${token}`);
  }

  function handleSelectLetter(l) {
    setLetter(l);
    searchParams.set("vetId", vetId);
    searchParams.set("token", token);
    searchParams.set("letra", l);
    setSearchParams(searchParams);
  }

  function handleVerPaciente(pet) {
    setSelectedPet(pet);
    setShowFicha(true);
  }

  async function handleVerCitas(pet) {
    setCitasPet(pet);
    setShowCitasModal(true);
  }

  function handleAddSuccess() {
    setShowAddModal(false);
    loadPacientes();
  }

  function handleEditPet(pet) {
    setEditingPet(pet);
    setShowEditModal(true);
  }

  function handleEditSuccess() {
    setShowEditModal(false);
    setEditingPet(null);
    loadPacientes();
    toast.success("Paciente actualizado");
  }

  function handleDeletePet(pet) {
    setDeletingPet(pet);
    setShowDeleteConfirm(true);
  }

  async function confirmDelete() {
    if (!deletingPet) return;
    try {
      await eliminarPaciente(vetId, deletingPet.id);
      setShowDeleteConfirm(false);
      setDeletingPet(null);
      loadPacientes();
      toast.success("Paciente eliminado");
    } catch (err) {
      toast.error("Error al eliminar paciente");
    }
  }

  if (loading) {
    return (
      <div style={{ backgroundColor: COLORS.background, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="text-center">
          <div className="spinner-border" style={{ color: COLORS.primary }} role="status" />
          <p className="mt-3" style={{ color: COLORS.textMuted }}>Cargando pacientes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: COLORS.background, minHeight: "100vh" }}>
        <BrandHeader />
        <div className="container py-5">
          <div className="alert" style={{ backgroundColor: "#fee2e2", color: "#991b1b", border: "none" }}>
            Error: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: "100vh" }}>
      {/* Header */}
      <BrandHeader />

      <div className="container py-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
          <div>
            <h3 style={{ color: COLORS.textDark, fontWeight: 600, marginBottom: "4px" }}>Mis Pacientes</h3>
            <p style={{ color: COLORS.textMuted, margin: 0, fontSize: "14px" }}>
              {pacientesExternos}/{LIMITE_EXTERNOS} pacientes externos
              {limiteAlcanzado && <span style={{ color: "#dc2626", marginLeft: "8px" }}>(Limite alcanzado)</span>}
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn"
              style={{ backgroundColor: COLORS.primary, color: "white", border: "none" }}
              onClick={() => setShowAddModal(true)}
              disabled={limiteAlcanzado}
            >
              + Agregar Paciente
            </button>
            <button
              className="btn"
              style={{ backgroundColor: COLORS.cardBg, color: COLORS.textDark, border: `1px solid ${COLORS.border}` }}
              onClick={handleVolver}
            >
              Regresar a Agenda
            </button>
          </div>
        </div>

        {/* Alphabet Filter */}
        <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: COLORS.cardBg }}>
          <div className="card-body">
            <div className="d-flex align-items-center flex-wrap gap-1">
              <span style={{ fontWeight: 500, color: COLORS.textDark, marginRight: "8px" }}>Filtrar:</span>
              {LETTERS.map((l) => (
                <button
                  key={l}
                  className="btn btn-sm"
                  style={{
                    backgroundColor: l === letter ? COLORS.primary : "transparent",
                    color: l === letter ? "white" : COLORS.textMuted,
                    border: `1px solid ${l === letter ? COLORS.primary : COLORS.border}`,
                    minWidth: "32px",
                    padding: "4px 8px",
                  }}
                  onClick={() => handleSelectLetter(l)}
                >
                  {l}
                </button>
              ))}
              <button
                className="btn btn-sm ms-2"
                style={{
                  backgroundColor: letter === "" ? COLORS.primaryLight : "transparent",
                  color: letter === "" ? COLORS.primaryDark : COLORS.textMuted,
                  border: `1px solid ${letter === "" ? COLORS.primary : COLORS.border}`,
                }}
                onClick={() => handleSelectLetter("")}
              >
                Todos
              </button>
            </div>
          </div>
        </div>

        {/* Patients List */}
        {pacientes.length === 0 ? (
          <div className="text-center py-5">
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>🐾</div>
            <p style={{ color: COLORS.textMuted }}>No se encontraron pacientes con esa inicial.</p>
          </div>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 g-3">
            {pacientes.map((pet) => (
              <div className="col" key={pet.id}>
                <div className="card h-100 border-0 shadow-sm" style={{ backgroundColor: COLORS.cardBg }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 style={{ color: COLORS.textDark, fontWeight: 600, marginBottom: 0 }}>
                        {pet.nombre || "Mascota sin nombre"}
                      </h5>
                      {pet.tipo === "lemmontag" ? (
                        <span style={{ backgroundColor: COLORS.primaryLight, color: COLORS.primaryDark, padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: 500 }}>
                          LemmonTag
                        </span>
                      ) : pet.tipo === "externo" ? (
                        <span style={{ backgroundColor: "#f3f4f6", color: "#6b7280", padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: 500 }}>
                          Externo
                        </span>
                      ) : (
                        <span style={{ backgroundColor: COLORS.primaryLight, color: COLORS.primaryDark, padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: 500 }}>
                          LemmonTag
                        </span>
                      )}
                    </div>

                    <p style={{ color: COLORS.textMuted, margin: "4px 0", fontSize: "14px" }}>
                      <strong>Especie:</strong> {pet.especie || "N/A"} | <strong>Raza:</strong> {pet.raza || "Desconocida"}
                    </p>
                    {pet.edad && <p style={{ color: COLORS.textMuted, margin: "4px 0", fontSize: "14px" }}><strong>Edad:</strong> {pet.edad} anios</p>}

                    {(pet.ownerName || pet.nombreDueno) && (
                      <div style={{ backgroundColor: COLORS.background, borderRadius: "8px", padding: "12px", marginTop: "12px" }}>
                        <p style={{ color: COLORS.textMuted, fontSize: "12px", marginBottom: "4px", fontWeight: 500 }}>PROPIETARIO</p>
                        <p style={{ color: COLORS.textDark, margin: "4px 0", fontSize: "14px" }}>{pet.ownerName || pet.nombreDueno}</p>
                        {(pet.ownerPhone || pet.telefonoDueno) && (
                          <p style={{ color: COLORS.textMuted, margin: "2px 0", fontSize: "13px" }}>{pet.ownerPhone || pet.telefonoDueno}</p>
                        )}
                      </div>
                    )}

                    {pet.tipo === "externo" && pet.notas && (
                      <p style={{ color: COLORS.textMuted, fontSize: "13px", marginTop: "8px", fontStyle: "italic" }}>
                        Notas: {pet.notas}
                      </p>
                    )}
                  </div>

                  <div className="card-footer bg-transparent border-0 d-flex justify-content-between align-items-center pb-3">
                    {/* Left side: Edit/Delete for external patients only */}
                    <div className="d-flex gap-2">
                      {pet.tipo === "externo" && (
                        <>
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: "#fef3c7", color: "#92400e", border: "none" }}
                            onClick={() => handleEditPet(pet)}
                          >
                            Editar
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: "#fee2e2", color: "#991b1b", border: "none" }}
                            onClick={() => handleDeletePet(pet)}
                          >
                            Eliminar
                          </button>
                        </>
                      )}
                    </div>

                    {/* Right side: View actions */}
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm"
                        style={{ backgroundColor: COLORS.primary, color: "white", border: "none" }}
                        onClick={() => handleVerPaciente(pet)}
                      >
                        Ver Paciente
                      </button>
                      <button
                        className="btn btn-sm"
                        style={{ backgroundColor: COLORS.cardBg, color: COLORS.textDark, border: `1px solid ${COLORS.border}` }}
                        onClick={() => handleVerCitas(pet)}
                      >
                        Ver Citas
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="text-center py-4" style={{ color: COLORS.textMuted }}>
        <small>© 2025 LemmonTags - Todos los derechos reservados</small>
      </footer>

      {/* Modal Ver Ficha */}
      {showFicha && <VerFichaPacienteModal petData={selectedPet} onClose={() => setShowFicha(false)} />}

      {/* Modal Agregar Paciente */}
      {showAddModal && <AddPatientModal vetId={vetId} onClose={() => setShowAddModal(false)} onSuccess={handleAddSuccess} />}

      {/* Modal Editar Paciente */}
      {showEditModal && editingPet && (
        <EditPatientModal
          vetId={vetId}
          patient={editingPet}
          onClose={() => { setShowEditModal(false); setEditingPet(null); }}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Modal Confirmar Eliminar */}
      {showDeleteConfirm && deletingPet && (
        <DeleteConfirmModal
          patient={deletingPet}
          onCancel={() => { setShowDeleteConfirm(false); setDeletingPet(null); }}
          onConfirm={confirmDelete}
        />
      )}

      {/* Modal Ver Citas del Paciente */}
      {showCitasModal && citasPet && (
        <VerCitasPacienteModal
          vetId={vetId}
          token={token}
          pet={citasPet}
          onClose={() => { setShowCitasModal(false); setCitasPet(null); }}
        />
      )}
    </div>
  );
}

/**
 * Brand Header Component
 */
function BrandHeader() {
  return (
    <header style={{
      backgroundColor: COLORS.cardBg,
      borderBottom: `1px solid ${COLORS.border}`,
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
    }}>
      <div className="container py-3">
        <div className="d-flex justify-content-between align-items-center">
          <a
            href="https://lemmontags.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "8px" }}
          >
            <div style={{
              backgroundColor: COLORS.primary,
              borderRadius: "8px",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <span style={{
              fontSize: "1.25rem",
              fontWeight: 600,
              color: COLORS.textDark,
              letterSpacing: "-0.025em"
            }}>
              LemmonTags
            </span>
          </a>
        </div>
      </div>
    </header>
  );
}

/**
 * Edit Patient Modal
 */
function EditPatientModal({ vetId, patient, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    nombre: patient.nombre || "",
    especie: patient.especie || "",
    raza: patient.raza || "",
    nombreDueno: patient.nombreDueno || "",
    telefonoDueno: patient.telefonoDueno || "",
    emailDueno: patient.emailDueno || "",
    notas: patient.notas || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.nombre.trim()) {
      setError("El nombre es requerido");
      return;
    }

    setLoading(true);
    try {
      await actualizarPaciente(vetId, patient.id, formData);
      onSuccess();
    } catch (err) {
      setError(err.message || "Error al actualizar");
    }
    setLoading(false);
  };

  return (
    <div className="position-fixed top-0 start-0 w-100 h-100" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div className="card shadow border-0" style={{ width: "100%", maxWidth: "500px", maxHeight: "calc(100vh - 40px)", display: "flex", flexDirection: "column" }}>
        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: COLORS.cardBg, borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
          <h5 className="mb-0" style={{ color: COLORS.textDark }}>Editar Paciente</h5>
          <button type="button" className="btn-close" onClick={onClose} disabled={loading} />
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 }}>
          <div className="card-body" style={{ overflowY: "auto", flex: 1 }}>
            {error && <div className="alert alert-danger py-2">{error}</div>}

            <div className="mb-3">
              <label className="form-label">Nombre <span className="text-danger">*</span></label>
              <input type="text" className="form-control" name="nombre" value={formData.nombre} onChange={handleChange} required />
            </div>

            <div className="mb-3">
              <label className="form-label">Especie <span className="text-danger">*</span></label>
              <select className="form-select" name="especie" value={formData.especie} onChange={handleChange} required>
                <option value="">Seleccionar...</option>
                <option value="perro">Perro</option>
                <option value="gato">Gato</option>
                <option value="ave">Ave</option>
                <option value="reptil">Reptil</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Raza</label>
              <input type="text" className="form-control" name="raza" value={formData.raza} onChange={handleChange} />
            </div>

            <hr />
            <p style={{ color: COLORS.textMuted, fontSize: "12px", fontWeight: 500 }}>DATOS DEL PROPIETARIO</p>

            <div className="mb-3">
              <label className="form-label">Nombre del dueno <span className="text-danger">*</span></label>
              <input type="text" className="form-control" name="nombreDueno" value={formData.nombreDueno} onChange={handleChange} required />
            </div>

            <div className="mb-3">
              <label className="form-label">Telefono <span className="text-danger">*</span></label>
              <input type="tel" className="form-control" name="telefonoDueno" value={formData.telefonoDueno} onChange={handleChange} required />
            </div>

            <div className="mb-3">
              <label className="form-label">Email</label>
              <input type="email" className="form-control" name="emailDueno" value={formData.emailDueno} onChange={handleChange} />
            </div>

            <div className="mb-3">
              <label className="form-label">Notas</label>
              <textarea className="form-control" name="notas" value={formData.notas} onChange={handleChange} rows={2} />
            </div>
          </div>

          <div className="card-footer d-flex justify-content-end gap-2" style={{ flexShrink: 0, borderTop: `1px solid ${COLORS.border}` }}>
            <button type="button" className="btn" style={{ backgroundColor: COLORS.cardBg, color: COLORS.textDark, border: `1px solid ${COLORS.border}` }} onClick={onClose} disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="btn" style={{ backgroundColor: COLORS.primary, color: "white", border: "none" }} disabled={loading}>
              {loading ? "Guardando..." : "Guardar Cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Delete Confirmation Modal
 */
function DeleteConfirmModal({ patient, onCancel, onConfirm }) {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  return (
    <div className="position-fixed top-0 start-0 w-100 h-100" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div className="card shadow border-0" style={{ width: "100%", maxWidth: "400px" }}>
        <div className="card-body text-center p-4">
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
          <h5 style={{ color: COLORS.textDark, marginBottom: "8px" }}>Eliminar Paciente</h5>
          <p style={{ color: COLORS.textMuted }}>
            ¿Estas seguro de eliminar a <strong>{patient.nombre}</strong>?
            <br />
            <small>Esta accion no se puede deshacer.</small>
          </p>

          <div className="d-flex justify-content-center gap-2 mt-4">
            <button
              className="btn"
              style={{ backgroundColor: COLORS.cardBg, color: COLORS.textDark, border: `1px solid ${COLORS.border}` }}
              onClick={onCancel}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              className="btn"
              style={{ backgroundColor: "#dc2626", color: "white", border: "none" }}
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? "Eliminando..." : "Si, Eliminar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VerFichaPacienteModal({ petData, onClose }) {
  if (!petData) return null;

  if (petData.tipo === "externo") {
    return (
      <div className="position-fixed top-0 start-0 w-100 h-100" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <div className="card shadow border-0" style={{ width: "100%", maxWidth: "500px", maxHeight: "calc(100vh - 40px)", overflow: "auto" }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: COLORS.cardBg, borderBottom: `1px solid ${COLORS.border}` }}>
            <h5 className="mb-0" style={{ color: COLORS.textDark }}>Ficha del Paciente</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="card-body">
            <span style={{ backgroundColor: "#f3f4f6", color: "#6b7280", padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: 500 }}>Paciente Externo</span>

            <div className="mt-3">
              <p style={{ margin: "8px 0", color: COLORS.textDark }}><strong>Nombre:</strong> {petData.nombre}</p>
              <p style={{ margin: "8px 0", color: COLORS.textDark }}><strong>Especie:</strong> {petData.especie}</p>
              {petData.raza && <p style={{ margin: "8px 0", color: COLORS.textDark }}><strong>Raza:</strong> {petData.raza}</p>}
            </div>

            <hr style={{ borderColor: COLORS.border }} />
            <p style={{ color: COLORS.textMuted, fontSize: "12px", fontWeight: 500, marginBottom: "8px" }}>PROPIETARIO</p>
            <p style={{ margin: "8px 0", color: COLORS.textDark }}><strong>Nombre:</strong> {petData.nombreDueno}</p>
            <p style={{ margin: "8px 0", color: COLORS.textDark }}><strong>Telefono:</strong> {petData.telefonoDueno}</p>
            {petData.emailDueno && <p style={{ margin: "8px 0", color: COLORS.textDark }}><strong>Email:</strong> {petData.emailDueno}</p>}

            {petData.notas && (
              <>
                <hr style={{ borderColor: COLORS.border }} />
                <p style={{ color: COLORS.textMuted, fontSize: "12px", fontWeight: 500, marginBottom: "8px" }}>NOTAS</p>
                <p style={{ color: COLORS.textMuted }}>{petData.notas}</p>
              </>
            )}
          </div>
          <div className="card-footer bg-transparent border-0 text-end">
            <button className="btn" style={{ backgroundColor: COLORS.cardBg, color: COLORS.textDark, border: `1px solid ${COLORS.border}` }} onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const fieldsForMascota = Object.keys(RECORD_FIELDS)
    .map((key) => ({ fieldKey: key, ...RECORD_FIELDS[key] }))
    .filter((f) => f.applicableTypes.includes("mascota"))
    .filter((f) => (f.visibleIn || []).includes("consultaDelVeterinario"));

  return (
    <div className="position-fixed top-0 start-0 w-100 h-100" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div className="card shadow border-0" style={{ width: "100%", maxWidth: "600px", maxHeight: "calc(100vh - 40px)", display: "flex", flexDirection: "column" }}>
        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: COLORS.cardBg, borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
          <h5 className="mb-0" style={{ color: COLORS.textDark }}>Ficha del Paciente</h5>
          <button type="button" className="btn-close" onClick={onClose} />
        </div>
        <div className="card-body" style={{ overflowY: "auto", flex: 1 }}>
          <span style={{ backgroundColor: COLORS.primaryLight, color: COLORS.primaryDark, padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: 500 }}>LemmonTag</span>
          <p style={{ fontStyle: "italic", fontSize: "0.9rem", color: COLORS.textMuted, marginTop: "8px" }}>Datos en modo lectura</p>
          <DynamicForm fields={fieldsForMascota} recordData={petData} readOnly />
        </div>
        <div className="card-footer bg-transparent border-0 text-end" style={{ flexShrink: 0 }}>
          <button className="btn" style={{ backgroundColor: COLORS.cardBg, color: COLORS.textDark, border: `1px solid ${COLORS.border}` }} onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal para ver las citas de un paciente
 */
function VerCitasPacienteModal({ vetId, token, pet, onClose }) {
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCitas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vetId, pet.id]);

  async function loadCitas() {
    setLoading(true);
    try {
      const allCitas = await getCitasDeVet(vetId);
      // Filtrar por objetoId y ordenar por fecha descendente
      const citasPaciente = allCitas
        .filter((c) => c.objetoId === pet.id)
        .sort((a, b) => {
          const dateA = new Date(a.fecha + "T" + (a.horaInicio || "00:00"));
          const dateB = new Date(b.fecha + "T" + (b.horaInicio || "00:00"));
          return dateB - dateA; // Descendente
        });
      setCitas(citasPaciente);
    } catch (err) {
      console.error("Error cargando citas:", err);
      toast.error("Error al cargar las citas");
    }
    setLoading(false);
  }

  function formatearFecha(fechaStr) {
    if (!fechaStr) return "Sin fecha";
    const fecha = new Date(fechaStr + "T00:00:00");
    return fecha.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  function esCitaPasada(cita) {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaCita = new Date(cita.fecha + "T23:59:59");
    return fechaCita < hoy;
  }

  function getEstadoBadge(estado) {
    const estilos = {
      confirmada: { bg: "#dcfce7", color: "#166534", texto: "Confirmada" },
      completada: { bg: "#dbeafe", color: "#1e40af", texto: "Completada" },
      pendiente: { bg: "#fef3c7", color: "#92400e", texto: "Pendiente" },
      pendiente_confirmacion: { bg: "#fef3c7", color: "#92400e", texto: "Por confirmar" },
      rechazada: { bg: "#fee2e2", color: "#991b1b", texto: "Cancelada" },
      no_asistio: { bg: "#f3f4f6", color: "#6b7280", texto: "No asistio" },
    };
    const estilo = estilos[estado] || { bg: "#f3f4f6", color: "#6b7280", texto: estado };
    return (
      <span style={{ backgroundColor: estilo.bg, color: estilo.color, padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: 500 }}>
        {estilo.texto}
      </span>
    );
  }

  function handleAtender(cita) {
    navigate(`/vetPanel/atender/${cita.id}?vetId=${vetId}&token=${token}`);
  }

  return (
    <div className="position-fixed top-0 start-0 w-100 h-100" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div className="card shadow border-0" style={{ width: "100%", maxWidth: "700px", maxHeight: "calc(100vh - 40px)", display: "flex", flexDirection: "column" }}>
        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: COLORS.cardBg, borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
          <h5 className="mb-0" style={{ color: COLORS.textDark }}>
            Citas de {pet.nombre || "Paciente"}
          </h5>
          <button type="button" className="btn-close" onClick={onClose} />
        </div>

        <div className="card-body" style={{ overflowY: "auto", flex: 1, padding: 0 }}>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{ color: COLORS.primary }} role="status" />
              <p className="mt-3" style={{ color: COLORS.textMuted }}>Cargando citas...</p>
            </div>
          ) : citas.length === 0 ? (
            <div className="text-center py-5">
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📅</div>
              <p style={{ color: COLORS.textMuted }}>No hay citas registradas para este paciente</p>
            </div>
          ) : (
            <div className="list-group list-group-flush">
              {citas.map((cita) => {
                const pasada = esCitaPasada(cita);
                const puedeAtender = cita.estado === "confirmada";

                return (
                  <div key={cita.id} className="list-group-item" style={{ borderLeft: "none", borderRight: "none", padding: "16px 20px" }}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <span style={{ color: COLORS.textDark, fontWeight: 500 }}>
                          📅 {formatearFecha(cita.fecha)}
                          {cita.horaInicio && <span style={{ color: COLORS.textMuted }}> - {cita.horaInicio}</span>}
                        </span>
                        {cita.motivo && (
                          <p style={{ color: COLORS.textMuted, margin: "4px 0 0 0", fontSize: "14px" }}>
                            {cita.motivo}
                          </p>
                        )}
                      </div>
                      {getEstadoBadge(cita.estado)}
                    </div>

                    <div className="d-flex gap-2 flex-wrap mt-2">
                      {/* Boton Cartilla - solo si existe URL */}
                      {cita.snapshotCartillaUrl && (
                        <a
                          href={cita.snapshotCartillaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm"
                          style={{ backgroundColor: COLORS.primaryLight, color: COLORS.primaryDark, border: "none" }}
                        >
                          📄 Cartilla
                        </a>
                      )}

                      {/* Boton Expediente - solo si existe URL */}
                      {cita.snapshotExpedienteUrl && (
                        <a
                          href={cita.snapshotExpedienteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm"
                          style={{ backgroundColor: "#dbeafe", color: "#1e40af", border: "none" }}
                        >
                          📋 Expediente
                        </a>
                      )}

                      {/* Boton Atender - solo si cita confirmada */}
                      {puedeAtender && (
                        <button
                          className="btn btn-sm"
                          style={{ backgroundColor: COLORS.primary, color: "white", border: "none" }}
                          onClick={() => handleAtender(cita)}
                        >
                          ✏️ Atender
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card-footer bg-transparent d-flex justify-content-end" style={{ flexShrink: 0, borderTop: `1px solid ${COLORS.border}` }}>
          <button className="btn" style={{ backgroundColor: COLORS.cardBg, color: COLORS.textDark, border: `1px solid ${COLORS.border}` }} onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
